import { createContext, useContext, useMemo, useRef, useCallback, type ReactNode } from 'react';
import { useSyncExternalStore } from 'react';

// ── Store ──────────────────────────────────────────────────

/** What a waiting region tells the registry about itself. */
export interface ZoneEntry {
  /** This region's spinner wins when several regions wait (#1561 W5). */
  primary: boolean;
  /** The region passes `onRetry`: a Try again exists for the screen. */
  hasRetry: boolean;
  /** Refetch exactly this region's queries. */
  retry: () => void;
}

export interface ZoneSnapshot {
  count: number;
  /** The one zone that draws the spinner (and Try again): the `primary` one, else one with a retry, else the earliest registered. */
  leaderId: string | null;
  /** When the current waiting episode began (ms, `Date.now()`); null when nothing waits. */
  startedAt: number | null;
  /** Some waiting zone has an `onRetry`. */
  hasRetry: boolean;
}

export interface ZoneStore {
  subscribe: (cb: () => void) => () => void;
  getSnapshot: () => ZoneSnapshot;
  register: (id: string, entry: ZoneEntry) => void;
  unregister: (id: string) => void;
  /** Run every waiting zone's refetch and restart the Try again clock. */
  retryAll: () => void;
}

export function createZoneStore(): ZoneStore {
  // Map keeps registration order: the earliest-registered zone is the first key.
  const zones = new Map<string, ZoneEntry>();
  const listeners = new Set<() => void>();
  let startedAt: number | null = null;
  // Bumped on every register/unregister so a deferred "the wait ended" check
  // can tell whether a zone came back in the same commit (a remount).
  let emptyGen = 0;
  let snapshot: ZoneSnapshot = { count: 0, leaderId: null, startedAt: null, hasRetry: false };

  function compute(): ZoneSnapshot {
    // Leader: `primary` first, then a zone that can draw Try again (a bare
    // LoadingOverlay inside a known region's skeleton registers first, and as
    // leader its spinner would have no Try again, #1561-f), then earliest.
    const rank = (e: ZoneEntry) => (e.primary ? 2 : 0) + (e.hasRetry ? 1 : 0);
    let leaderId: string | null = null;
    let leaderRank = -1;
    let hasRetry = false;
    for (const [id, entry] of zones) {
      if (rank(entry) > leaderRank) {
        leaderId = id;
        leaderRank = rank(entry);
      }
      if (entry.hasRetry) hasRetry = true;
    }
    return { count: zones.size, leaderId, startedAt, hasRetry };
  }

  function emit() {
    snapshot = compute();
    listeners.forEach((cb) => cb());
  }

  return {
    subscribe(cb) {
      listeners.add(cb);
      return () => listeners.delete(cb);
    },
    getSnapshot() {
      return snapshot;
    },
    register(id, entry) {
      emptyGen += 1;
      const existing = zones.get(id);
      if (existing && existing.primary === entry.primary && existing.hasRetry === entry.hasRetry) {
        zones.set(id, entry);
        return;
      }
      zones.set(id, entry);
      if (startedAt === null) startedAt = Date.now();
      emit();
    },
    unregister(id) {
      if (!zones.delete(id)) return;
      emptyGen += 1;
      if (zones.size === 0) {
        // The wait is over only if no zone re-registers in this same commit
        // (a remount unregisters then registers synchronously). Microtask, not
        // a timer, so fake timers cannot stall it.
        const gen = emptyGen;
        queueMicrotask(() => {
          if (gen === emptyGen && zones.size === 0 && startedAt !== null) {
            // Silent: nothing is waiting, so no subscriber has anything to
            // redraw (a notify here would be a state update outside act).
            startedAt = null;
            snapshot = { ...snapshot, startedAt: null };
          }
        });
      }
      emit();
    },
    retryAll() {
      const entries = [...zones.values()];
      startedAt = Date.now();
      emit();
      entries.forEach((e) => e.retry());
    },
  };
}

// ── Context ────────────────────────────────────────────────

interface LoadingZoneContextValue {
  store: ZoneStore;
}

const fallbackZoneStore = createZoneStore();

const LoadingZoneCtx = createContext<LoadingZoneContextValue | null>(null);

export function LoadingZoneProvider({ children }: { children: ReactNode }) {
  const storeRef = useRef<ZoneStore | null>(null);
  if (!storeRef.current) {
    storeRef.current = createZoneStore();
  }
  const store = storeRef.current;

  // Memo the value object so consumers' useContext doesn't see a new reference
  // on every parent render. `store` itself is stable (ref-held singleton).
  const value = useMemo<LoadingZoneContextValue>(() => ({ store }), [store]);

  return (
    <LoadingZoneCtx.Provider value={value}>
      {children}
    </LoadingZoneCtx.Provider>
  );
}

// ── Hooks ──────────────────────────────────────────────────

/**
 * Read the current zone count. Returns number of active zones.
 * Triggers re-render when zone count changes.
 */
export function useLoadingZoneCount(): number {
  const ctx = useContext(LoadingZoneCtx);
  const store = ctx?.store ?? fallbackZoneStore;
  const subscribe = useCallback(
    (cb: () => void) => store.subscribe(cb),
    [store],
  );
  const getSnapshot = useCallback(
    () => store.getSnapshot().count,
    [store],
  );
  return useSyncExternalStore(subscribe, getSnapshot);
}

/**
 * Access zone store for registration. Returns null outside provider.
 */
export function useLoadingZoneStore(): ZoneStore | null {
  const ctx = useContext(LoadingZoneCtx);
  return ctx?.store ?? null;
}
